// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable prefer-const */

import React from "react";
import { MapRef, useMap } from "react-map-gl/mapbox";
import { useData } from "../StateManagement";
import * as turf from "@turf/turf";
import MapboxGl from "mapbox-gl";
import type { LngLat, LngLatBounds, LngLatBoundsLike, PaddingOptions } from "mapbox-gl";
import { ControlGroup } from ".";

const getLineToFit = (coords: number[][], viewportBearing: number, orientation: "vertical" | "horizontal") => {
	let llToUl = turf.bearing(coords[0], coords[1]);
	let ulToUr = turf.bearing(coords[1], coords[2]);
	let urToLr = turf.bearing(coords[2], coords[3]);
	let lrToLl = turf.bearing(coords[3], coords[0]);

	if (viewportBearing < 0) viewportBearing = 360 - Math.abs(viewportBearing);
	if (llToUl < 0) llToUl = 360 - Math.abs(llToUl);
	if (ulToUr < 0) ulToUr = 360 - Math.abs(ulToUr);
	if (urToLr < 0) urToLr = 360 - Math.abs(urToLr);
	if (lrToLl < 0) lrToLl = 360 - Math.abs(lrToLl);

	let closestBearing = [llToUl, ulToUr, urToLr, lrToLl].reduce((prev, curr) =>
		Math.abs(curr - viewportBearing) < Math.abs(prev - viewportBearing) ? curr : prev,
	);

	let firstPoint: GeoJSON.Position, secondPoint: GeoJSON.Position;

	if (orientation === "vertical") {
		if (closestBearing.toPrecision(4) === llToUl.toPrecision(4)) {
			firstPoint = turf.midpoint(coords[0], coords[3]).geometry.coordinates;
			secondPoint = turf.midpoint(coords[1], coords[2]).geometry.coordinates;
		}
		if (closestBearing.toPrecision(4) === ulToUr.toPrecision(4)) {
			firstPoint = turf.midpoint(coords[0], coords[1]).geometry.coordinates;
			secondPoint = turf.midpoint(coords[2], coords[3]).geometry.coordinates;
		}
		if (closestBearing.toPrecision(4) === urToLr.toPrecision(4)) {
			firstPoint = turf.midpoint(coords[0], coords[3]).geometry.coordinates;
			secondPoint = turf.midpoint(coords[1], coords[2]).geometry.coordinates;
		}
		if (closestBearing.toPrecision(4) === lrToLl.toPrecision(4)) {
			firstPoint = turf.midpoint(coords[0], coords[1]).geometry.coordinates;
			secondPoint = turf.midpoint(coords[2], coords[3]).geometry.coordinates;
		}
	} else if (orientation === "horizontal") {
		if (closestBearing.toPrecision(4) === llToUl.toPrecision(4)) {
			firstPoint = turf.midpoint(coords[0], coords[1]).geometry.coordinates;
			secondPoint = turf.midpoint(coords[2], coords[3]).geometry.coordinates;
		}
		if (closestBearing.toPrecision(4) === ulToUr.toPrecision(4)) {
			firstPoint = turf.midpoint(coords[1], coords[2]).geometry.coordinates;
			secondPoint = turf.midpoint(coords[0], coords[3]).geometry.coordinates;
		}
		if (closestBearing.toPrecision(4) === urToLr.toPrecision(4)) {
			firstPoint = turf.midpoint(coords[0], coords[1]).geometry.coordinates;
			secondPoint = turf.midpoint(coords[2], coords[3]).geometry.coordinates;
		}
		if (closestBearing.toPrecision(4) === lrToLl.toPrecision(4)) {
			firstPoint = turf.midpoint(coords[1], coords[2]).geometry.coordinates;
			secondPoint = turf.midpoint(coords[0], coords[3]).geometry.coordinates;
		}
	}

	if (closestBearing > 180) closestBearing -= 360;

	// let bearing = turf.bearing(coords[0], coords[1]);
	return { bounds: turf.bbox(turf.multiLineString([[firstPoint!, secondPoint!]])), closestBearing };
};

const fitBoundsRotated = (
	bounds: LngLatBoundsLike | LngLatBounds,
	options: {
		padding?: number | PaddingOptions;
		offset?: [number, number];
		maxZoom?: number;
		bearing?: number;
		zoom?: number;
		center?: LngLat;
		linear?: boolean;
	},
	map: MapRef,
) => {
	map.stop();
	bounds = MapboxGl.LngLatBounds.convert(bounds);

	const cameraBounds = map.cameraForBounds(bounds, { padding: 0, pitch: 0, bearing: options.bearing });
	const padding = map.getPadding();
	const newPadding = { ...padding, top: padding.top! + 55 };

	// Get the map's container dimensions
	const container = map.getContainer();
	const containerWidth = container.offsetWidth - padding.left! - padding.right!;
	const containerHeight = container.offsetHeight - padding.top! - padding.bottom!;
	// Get bounding box dimensions in px
	const sw = map.project(bounds.getSouthWest());
	const ne = map.project(bounds.getNorthEast());
	const bboxWidth = Math.abs(sw.x - ne.x);
	const bboxHeight = Math.abs(sw.y - ne.y);
	// calculate optimal zoom
	const scaleWidth = containerWidth / bboxWidth;
	const scaleHeight = containerHeight / bboxHeight;
	const scale = Math.min(scaleWidth, scaleHeight);
	const optimalZoom = map.getZoom() + Math.log2(scale);
	// calculate offset in case padding uneven in either direction
	// let offset: [number, number] | undefined;
	// if (padding.left !== padding.right || padding.top !== padding.bottom) {
	// 	offset = [0, padding.bottom! - padding.top!];
	// }
	// pan map to camera bounds then apply any padding offset
	map.easeTo({
		...cameraBounds,
		padding: newPadding,
		zoom: optimalZoom,
		retainPadding: false,
		duration: 800,
	});
	// if (offset) {
	// 	map.once("moveend", () => {
	// 		map.panBy(offset, {
	// 			duration: 200
	// 		});
	// 	});
	// }
};

export const FitToHeight = () => {
	const { MFAViewer: mapRef } = useMap();
	const { trialPolygon } = useData();

	const fitBounds = () => {
		if (mapRef && trialPolygon) {
			const coords = turf.coordAll(trialPolygon);
			let viewportBearing = mapRef.getBearing();

			const { bounds, closestBearing } = getLineToFit(coords, viewportBearing, "vertical");

			fitBoundsRotated(
				[
					[bounds[0], bounds[1]],
					[bounds[2], bounds[3]],
				],
				{
					bearing: closestBearing,
				},
				mapRef,
			);
		}
	};

	return <ControlGroup.Action label="Fit to Height" icon="fit height" onClick={fitBounds} />;
};

export const FitToWidth = () => {
	const { MFAViewer: mapRef } = useMap();
	const { trialPolygon } = useData();

	const fitBounds = () => {
		if (mapRef && trialPolygon) {
			const coords = turf.coordAll(trialPolygon);
			let viewportBearing = mapRef.getBearing();

			const { bounds, closestBearing } = getLineToFit(coords, viewportBearing, "horizontal");

			fitBoundsRotated(
				[
					[bounds[0], bounds[1]],
					[bounds[2], bounds[3]],
				],
				{
					bearing: closestBearing,
				},
				mapRef,
			);
		}
	};

	return <ControlGroup.Action label="Fit to Width" icon="fit width" onClick={fitBounds} />;
};

type RotateProps = {
	direction: "right" | "left";
};

export const Rotate = ({ direction }: RotateProps) => {
	const { MFAViewer: mapRef } = useMap();
	const { trialPolygon } = useData();

	const rotate = (direction: "right" | "left") => {
		if (mapRef && trialPolygon) {
			const coords = turf.coordAll(trialPolygon);
			let viewportBearing = mapRef.getBearing();

			let llToUl = turf.bearing(coords[0], coords[1]);
			let ulToUr = turf.bearing(coords[1], coords[2]);
			let urToLr = turf.bearing(coords[2], coords[3]);
			let lrToLl = turf.bearing(coords[3], coords[0]);

			if (viewportBearing < 0) viewportBearing = 360 - Math.abs(viewportBearing);
			if (llToUl < 0) llToUl = 360 - Math.abs(llToUl);
			if (ulToUr < 0) ulToUr = 360 - Math.abs(ulToUr);
			if (urToLr < 0) urToLr = 360 - Math.abs(urToLr);
			if (lrToLl < 0) lrToLl = 360 - Math.abs(lrToLl);

			let closestBearing = [llToUl, ulToUr, urToLr, lrToLl].reduce((prev, curr) =>
				Math.abs(curr - viewportBearing) < Math.abs(prev - viewportBearing) ? curr : prev,
			);

			const bounds = turf.bbox(trialPolygon);
			fitBoundsRotated(
				[
					[bounds[0], bounds[1]],
					[bounds[2], bounds[3]],
				],
				{
					bearing: (closestBearing + (direction === "right" ? 270 : 90)) % 360,
				},
				mapRef,
			);
		}
	};

	return (
		<ControlGroup.Action
			label={`Rotate ${direction === "right" ? "Right" : "Left"}`}
			icon={`rotate ${direction}`}
			onClick={() => {
				rotate(direction);
			}}
		/>
	);
};

export const ReCenter = () => {
	const { MFAViewer: mapRef } = useMap();
	const { trialPolygon } = useData();

	const recenter = () => {
		if (mapRef && trialPolygon) {
			const coords = turf.coordAll(trialPolygon);
			let viewportBearing = mapRef.getBearing();

			let llToUl = turf.bearing(coords[0], coords[1]);
			let ulToUr = turf.bearing(coords[1], coords[2]);
			let urToLr = turf.bearing(coords[2], coords[3]);
			let lrToLl = turf.bearing(coords[3], coords[0]);

			if (viewportBearing < 0) viewportBearing = 360 - Math.abs(viewportBearing);
			if (llToUl < 0) llToUl = 360 - Math.abs(llToUl);
			if (ulToUr < 0) ulToUr = 360 - Math.abs(ulToUr);
			if (urToLr < 0) urToLr = 360 - Math.abs(urToLr);
			if (lrToLl < 0) lrToLl = 360 - Math.abs(lrToLl);

			let closestBearing = [llToUl, ulToUr, urToLr, lrToLl].reduce((prev, curr) =>
				Math.abs(curr - viewportBearing) < Math.abs(prev - viewportBearing) ? curr : prev,
			);

			const bounds = turf.bbox(trialPolygon);
			fitBoundsRotated(
				[
					[bounds[0], bounds[1]],
					[bounds[2], bounds[3]],
				],
				{
					bearing: closestBearing,
				},
				mapRef,
			);
		}
	};

	return <ControlGroup.Action label="Re-center" icon="compass outline" onClick={recenter} />;
};
